import { useState, type KeyboardEvent } from 'react'
import { Button, Chip } from '@heroui/react'
import { api } from '../lib/api'
import { deliveredName } from '../lib/delivered'
import { listState } from '../lib/listState'
import type { DeliveredOutcome, DeliveredService, Service } from '../lib/types'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { CODE_RE, CodeField, MoveButtons, NamesField, cleanNames, hasName, inOrder, reorder } from './recordParts'
import { Toggle } from './sectionKit'

/**
 * Delivered services: the tree under one service.
 *
 * The customer chose the service at the kiosk; these are the specific things the
 * desk may say it did within it — "Close joint account", "Print a statement" — with
 * outcomes under the ones that can end more than one way. The rule to know before
 * adding the first, and the reason the line at the top of the tree says it: a
 * service with any active delivered service requires the desk to record one before
 * the customer is finished. There is no switch, for the reason outcomes have none.
 *
 * It is drawn inside the service's own drawer, so a row here opens in place rather
 * than into a drawer of its own: a drawer over a drawer is a form inside a form,
 * and Enter in one would save the other. For the same reason every button here is
 * type="button" and Enter in a box is kept from reaching the service's form.
 *
 * Every component here is defined at module level. A component defined inside
 * another's render is a new component on every render, so React remounts it and
 * the text box being typed into loses focus after one character — a bug this
 * codebase has shipped before.
 */

/** Runs one administration call and says what happened; Services' useAction. */
export type Run = (fn: () => Promise<unknown>, said: string) => Promise<void>

type Row = { id?: string; code: string; name: Record<string, string>; sortOrder: number; active: boolean }

const blank = (): Row => ({ code: '', name: {}, sortOrder: 0, active: true })

/** Enter in a box here must not submit the service's drawer around the tree. */
const keepEnter = (e: KeyboardEvent) => {
  if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') e.preventDefault()
}

export function DeliveredTree({
  service,
  rows,
  loaded,
  busy,
  run,
}: {
  service: Service
  rows: DeliveredService[]
  /** False until the tree's first answer, when an empty `rows` means only "not read yet". */
  loaded: boolean
  busy: boolean
  run: Run
}) {
  const { say } = useStaffText()
  const [adding, setAdding] = useState(false)
  const [openId, setOpenId] = useState('')
  const active = inOrder(rows.filter((d) => d.active))
  const retired = inOrder(rows.filter((d) => !d.active))
  const state = listState(loaded, rows.length)

  const move = (id: string, by: number) =>
    void run(async () => {
      for (const row of reorder(active, id, by)) await api.updateDeliveredService(row.id, { sortOrder: row.sortOrder })
    }, say('record.orderSaved'))

  return (
    <section className="field record-tree" style={{ gridColumn: '1 / -1' }} onKeyDown={keepEnter}>
      <div className="record-tree__head">
        <span className="record-tree__title">{say('record.delivered.title')}</span>
        {!adding && (
          <Button variant="secondary" size="sm" onPress={() => setAdding(true)}>
            <Icon name="plus" size={14} /> {say('record.add')}
          </Button>
        )}
      </div>
      {/* A status region that stays mounted, so the change from loading to the
          line under it is read out. */}
      <small role="status">
        {state === 'loading'
          ? say('common.loading')
          : active.length > 0
            ? say('record.delivered.required')
            : say('record.delivered.none')}
      </small>

      {adding && (
        <RowEditor
          row={blank()}
          siblings={rows}
          isNew
          busy={busy}
          onCancel={() => setAdding(false)}
          onSave={(draft) =>
            void run(async () => {
              await api.createDeliveredService({
                ...draft,
                name: cleanNames(draft.name),
                serviceId: service.id,
                sortOrder: (active.at(-1)?.sortOrder ?? 0) + 10,
              })
              setAdding(false)
            }, say('record.delivered.added'))
          }
        />
      )}

      {rows.length > 0 && (
        <ul className="record-tree__rows">
          {[...active, ...retired].map((d) => (
            <DeliveredRow
              // Keyed on what was saved as well as the id, so a row whose answer
              // comes back changed starts again from it rather than keeping a
              // draft of what it used to be. Typing never changes the key.
              key={`${d.id}:${d.code}:${d.active}`}
              delivered={d}
              siblings={rows}
              open={openId === d.id}
              onToggle={() => setOpenId(openId === d.id ? '' : d.id)}
              movable={d.active}
              first={d.id === active[0]?.id}
              last={d.id === active.at(-1)?.id}
              onMove={(by) => move(d.id, by)}
              busy={busy}
              run={run}
            />
          ))}
        </ul>
      )}
    </section>
  )
}

function DeliveredRow({
  delivered,
  siblings,
  open,
  onToggle,
  movable,
  first,
  last,
  onMove,
  busy,
  run,
}: {
  delivered: DeliveredService
  siblings: DeliveredService[]
  open: boolean
  onToggle: () => void
  movable: boolean
  first: boolean
  last: boolean
  onMove: (by: number) => void
  busy: boolean
  run: Run
}) {
  const { lang, say } = useStaffText()
  const outcomes = delivered.outcomes ?? []
  const offered = outcomes.filter((o) => o.active).length
  const name = deliveredName(delivered, lang)

  return (
    <li className="record-tree__item" data-off={!delivered.active || undefined}>
      <div className="record-tree__row">
        <button type="button" className="record-tree__open" aria-expanded={open} onClick={onToggle}>
          <Icon name={open ? 'down' : 'right'} size={14} />
          <span className="record-tree__name">{name}</span>
          <span className="record-tree__code">{delivered.code}</span>
          {!delivered.active && (
            <Chip size="sm" variant="soft">
              {say('record.retired')}
            </Chip>
          )}
          <span className="record-tree__n">{offered > 0 ? say('record.delivered.nOutcomes', { n: offered }) : ''}</span>
        </button>
        {movable && <MoveButtons name={name} first={first} last={last} disabled={busy} onMove={onMove} />}
      </div>
      {open && (
        <div className="record-tree__body">
          <RowEditor
            row={delivered}
            siblings={siblings}
            isNew={false}
            busy={busy}
            onCancel={onToggle}
            onSave={(draft) =>
              void run(
                () =>
                  api.updateDeliveredService(delivered.id, {
                    code: draft.code,
                    name: cleanNames(draft.name),
                    active: draft.active,
                  }),
                say('record.saved'),
              )
            }
          />
          <OutcomeList delivered={delivered} busy={busy} run={run} />
        </div>
      )}
    </li>
  )
}

function OutcomeList({ delivered, busy, run }: { delivered: DeliveredService; busy: boolean; run: Run }) {
  const { lang, say } = useStaffText()
  const [adding, setAdding] = useState(false)
  const [openId, setOpenId] = useState('')
  const outcomes = delivered.outcomes ?? []
  const active = inOrder(outcomes.filter((o) => o.active))
  const retired = inOrder(outcomes.filter((o) => !o.active))

  const move = (id: string, by: number) =>
    void run(async () => {
      for (const row of reorder(active, id, by)) await api.updateDeliveredOutcome(row.id, { sortOrder: row.sortOrder })
    }, say('record.orderSaved'))

  return (
    <div className="record-tree__outcomes">
      <div className="record-tree__head">
        <span className="record-tree__subtitle">{say('record.delivered.outcomes')}</span>
        {!adding && (
          <Button variant="ghost" size="sm" onPress={() => setAdding(true)}>
            <Icon name="plus" size={14} /> {say('record.add')}
          </Button>
        )}
      </div>
      {outcomes.length === 0 && !adding && <small>{say('record.delivered.noOutcomes')}</small>}
      {adding && (
        <RowEditor
          row={blank()}
          siblings={outcomes}
          isNew
          busy={busy}
          onCancel={() => setAdding(false)}
          onSave={(draft) =>
            void run(async () => {
              await api.createDeliveredOutcome(delivered.id, {
                ...draft,
                name: cleanNames(draft.name),
                sortOrder: (active.at(-1)?.sortOrder ?? 0) + 10,
              })
              setAdding(false)
            }, say('record.outcomes.added'))
          }
        />
      )}
      {outcomes.length > 0 && (
        <ul className="record-tree__rows">
          {[...active, ...retired].map((o) => (
            <OutcomeRow
              key={`${o.id}:${o.code}:${o.active}`}
              outcome={o}
              siblings={outcomes}
              open={openId === o.id}
              onToggle={() => setOpenId(openId === o.id ? '' : o.id)}
              first={o.id === active[0]?.id}
              last={o.id === active.at(-1)?.id}
              onMove={(by) => move(o.id, by)}
              busy={busy}
              run={run}
              name={deliveredName(o, lang)}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function OutcomeRow({
  outcome,
  siblings,
  open,
  onToggle,
  first,
  last,
  onMove,
  busy,
  run,
  name,
}: {
  outcome: DeliveredOutcome
  siblings: DeliveredOutcome[]
  open: boolean
  onToggle: () => void
  first: boolean
  last: boolean
  onMove: (by: number) => void
  busy: boolean
  run: Run
  name: string
}) {
  const { say } = useStaffText()
  return (
    <li className="record-tree__item" data-off={!outcome.active || undefined}>
      <div className="record-tree__row">
        <button type="button" className="record-tree__open" aria-expanded={open} onClick={onToggle}>
          <Icon name={open ? 'down' : 'right'} size={14} />
          <span className="record-tree__name">{name}</span>
          <span className="record-tree__code">{outcome.code}</span>
          {!outcome.active && (
            <Chip size="sm" variant="soft">
              {say('record.retired')}
            </Chip>
          )}
        </button>
        {outcome.active && <MoveButtons name={name} first={first} last={last} disabled={busy} onMove={onMove} />}
      </div>
      {open && (
        <div className="record-tree__body">
          <RowEditor
            row={outcome}
            siblings={siblings}
            isNew={false}
            busy={busy}
            onCancel={onToggle}
            onSave={(draft) =>
              void run(
                () =>
                  api.updateDeliveredOutcome(outcome.id, {
                    code: draft.code,
                    name: cleanNames(draft.name),
                    active: draft.active,
                  }),
                say('record.saved'),
              )
            }
          />
        </div>
      )}
    </li>
  )
}

/** The editor a row opens into, in place: name, code, and whether it is offered. */
function RowEditor({
  row,
  siblings,
  isNew,
  busy,
  onCancel,
  onSave,
}: {
  row: Row
  siblings: Array<{ id: string; code: string }>
  isNew: boolean
  busy: boolean
  onCancel: () => void
  onSave: (draft: Row) => void
}) {
  const { say } = useStaffText()
  const [draft, setDraft] = useState<Row>({ ...row, name: { ...row.name } })
  const taken = draft.code !== '' && siblings.some((s) => s.id !== row.id && s.code === draft.code)
  const valid = CODE_RE.test(draft.code) && !taken && hasName(draft.name)
  const dirty = JSON.stringify(draft) !== JSON.stringify({ ...row, name: { ...row.name } })

  return (
    <div className="record-inline">
      <NamesField legend={say('record.name')} value={draft.name} onChange={(name) => setDraft({ ...draft, name })} />
      <CodeField
        value={draft.code}
        onChange={(code) => setDraft({ ...draft, code })}
        taken={taken}
        isNew={isNew}
        placeholder=""
      />
      {!isNew && (
        <Toggle
          title={say('record.offeredAtDesk')}
          hint={say('record.retireHint')}
          checked={draft.active}
          onChange={(active) => setDraft({ ...draft, active })}
        />
      )}
      <div className="actions">
        <Button variant="ghost" size="sm" onPress={onCancel}>
          {say('common.cancel')}
        </Button>
        <Button variant="secondary" size="sm" isDisabled={busy || !valid || (!isNew && !dirty)} onPress={() => onSave(draft)}>
          {isNew ? say('record.add') : say('record.save')}
        </Button>
      </div>
    </div>
  )
}
